import styled from '@emotion/styled';
import React from 'react';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import PlusCourseCard from '@comp/PlusCourseCard/PlusCourseCard';
import { isUpcoming } from '@models/Item';
import { isCompleted, isOngoing } from '@models/Course';
import { mediaQuery, Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import { segment } from '@actions/analytics';
import { wordsToSentence } from '@utils/textHelper';
import { GOAL_PAGE, SEE_ALL_CLICKED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { getEventMeta } from '@utils/cmsEventDedup';
import useWindowSize from '@hooks/useWindowSize';
import CardCarousel from './CardCarousel';

const SecWrapper = styled.section`
  position: relative;
  margin: auto;
  margin-top: 120px;
  width: 1376px;
  overflow: hidden;
  ${mediaQuery.uptoTablet} {
    width: 728px;
    margin-top: 32px;
  }
  ${mediaQuery.uptoMobile} {
    margin-top: 56px;
    width: 100%;
  }
`;

const CardWrapper = styled.div`
  margin-right: ${Spacings.SPACING_6B};
  width: 272px;
`;

const FreeClassSection = (props) => {
  const { block } = props;
  const {
    asPath,
    pathname,
    query: { goalUID, goalSlug }
  } = useRouter();
  const goalEvent = useGoalEventProps({ goalUID });
  const { isTablet, isMobile } = useWindowSize();

  const onCardClick = async (course) => {
    const { send_to_crm } = await getEventMeta('Course Viewed', {
      goal_uid: goalEvent?.goal_uid,
      course_uid: course.uid
    });
    segment.track('Course Viewed', {
      last_primary_source: GOAL_PAGE,
      last_primary_source_section: 'Live Class Carousel',
      course_uid: course.uid,
      course_title: course.name,
      start_date: course.startsAt,
      end_date: course.endsAt,
      educator_uid: wordsToSentence(course.authors, 'uid', ',', ','),
      educator_username: wordsToSentence(course.authors, 'username', ',', ','),
      ...goalEvent,
      send_to_crm
    });
  };

  const handleClickSeeAll = () => {
    segment.track(SEE_ALL_CLICKED, {
      ...goalEvent,
      last_primary_source: GOAL_PAGE,
      last_primary_source_section: 'Free Live Classes'
    });
  };

  return (
    <SecWrapper>
      <CardCarousel
        label="Free classes"
        subLabel="Watch free classes from top educators"
        step={block.data && !(isTablet || isMobile) ? 3 : 2}
        href={`${pathname}/classes`}
        as={`${asPath}/classes`}
        dataAnalytics={block.type}
        type={block.type}
        handleSeeAll={handleClickSeeAll}
      >
        {block.data.map((course) => {
          const formattedClass = {
            ...course,
            authors: [
              {
                ...course.author,
                name: `${course.author.firstName} ${course.author.lastName}`
              }
            ],
            nextTrack: course.nextSession,
            language: course.languageDisplay,
            colorCode: getTopicGroupAssetObj(course.topicGroups[0]?.name).color,
            isUpcoming: course.startsAt && isUpcoming(course.startsAt),
            isOngoing:
              course.startsAt &&
              course.endsAt &&
              isOngoing(course.startsAt, course.endsAt),
            isCompleted:
              course.isCompleted ||
              (course.endsAt && isCompleted(course.endsAt)),
            thumbnail: course.coverPhoto,
            goal: {
              uid: goalUID,
              slug: goalSlug
            }
          };
          return (
            <CardWrapper key={course.uid}>
              <PlusCourseCard
                course={formattedClass}
                showScheduleInfo
                orientation="vertical"
                label="live class carousel"
                onCardClick={() => onCardClick(formattedClass)}
                hideLessonCount
              />
            </CardWrapper>
          );
        })}
      </CardCarousel>
    </SecWrapper>
  );
};

export default FreeClassSection;
